import React from 'react';
import { Search, Flame, Trophy, Users, MessagesSquare, Clock } from 'lucide-react';
import { useRobloky } from '../../context/RoblokyContext';
import { GameCard } from './GameCard';
import type { GamesSort } from '../../types';

const SORTS: { id: GamesSort; label: string; icon: React.ElementType }[] = [
  { id: 'latest', label: 'Newest', icon: Clock },
  { id: 'top', label: 'Top liked', icon: Trophy },
  { id: 'players', label: 'Most played', icon: Users },
  { id: 'discussed', label: 'Discussed', icon: MessagesSquare },
];

export const GamesGrid: React.FC = () => {
  const {
    games,
    gamesLoading,
    gamesLoadingMore,
    gamesHasMore,
    gamesQuery,
    setGamesQuery,
    gamesSort,
    setGamesSort,
    loadMoreGames,
    refreshGames,
  } = useRobloky();

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex flex-col gap-3">
        <div className="relative md:hidden">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search games, creators, genres..."
            value={gamesQuery}
            onChange={(e) => setGamesQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h1 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white">
            <Flame className="h-5 w-5 text-red-400" />
            Discover Roblox Games
          </h1>
          <div className="flex items-center gap-1.5 flex-wrap">
            {SORTS.map((s) => {
              const Icon = s.icon;
              const active = gamesSort === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setGamesSort(s.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-mono font-bold transition-all border ${
                    active
                      ? 'bg-red-500/15 text-red-300 border-red-500/40'
                      : 'text-slate-400 hover:text-slate-200 border-white/10 hover:border-white/20'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {gamesLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[#0d111a] overflow-hidden animate-pulse">
              <div className="aspect-video bg-slate-800/60" />
              <div className="p-3 space-y-2">
                <div className="h-4 w-3/4 rounded bg-slate-800/60" />
                <div className="h-3 w-1/2 rounded bg-slate-800/60" />
              </div>
            </div>
          ))}
        </div>
      ) : games.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <p className="text-slate-300 font-bold">
            {gamesQuery ? `No games found for "${gamesQuery}"` : 'No games yet'}
          </p>
          <p className="text-xs font-mono text-slate-500">
            {gamesQuery ? 'Try another title, creator or genre.' : 'New scouted games appear here automatically.'}
          </p>
          {!gamesQuery && (
            <button
              onClick={refreshGames}
              className="mt-2 px-4 py-2 rounded-xl bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/25 transition-all"
            >
              Reload
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {games.map((g) => (
              <GameCard key={g.placeId} game={g} />
            ))}
          </div>
          {gamesHasMore && (
            <div className="text-center pt-2">
              <button
                onClick={loadMoreGames}
                disabled={gamesLoadingMore}
                className="px-6 py-2.5 rounded-xl bg-slate-900 border border-white/10 text-slate-200 text-xs font-bold hover:border-red-500/40 hover:text-red-300 transition-all disabled:opacity-50"
              >
                {gamesLoadingMore ? 'Loading...' : 'Load more games'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
